/**
 * COMPTABILITÉ — CE QU'UNE CARTE « LES DERNIÈRES SOIRÉES » LIT POUR SES
 * CHIFFRES (11/10/2026).
 *
 * Module PUR — ni React ni réseau : les lectures lui sont données (en
 * production, `lireFicheNuit` et `lireClotureDeNuit`, celles de la fiche de
 * nuit), ce qui le rend testable sans navigateur.
 *
 * Deux choses, et rien d'autre :
 *
 *   · `lireChiffresDeCarte` — la fiche de la nuit, puis la clôture qu'elle
 *     nomme, puis le modèle de la fiche (`chiffresDeLaCarte`). La clôture fait
 *     foi dès qu'elle existe : ILLISIBLE (coupure, 429 du quota global, 500),
 *     la carte se TAIT. Elle calculait sur les rapports seuls et gardait ce
 *     chiffre, sur une autre base que la fiche ouverte une seconde plus tard
 *     (une nuit finalisée y devenait « d'après les rapports », sous la
 *     pastille « Finalisée »).
 *
 *   · `fileDeLectures` — une file partagée par les sept cartes : peu de
 *     lectures de nuit à la fois, dans l'ordre où on les demande (l'ordre
 *     d'affichage). Chaque lecture de nuit coûte une dizaine de requêtes SQL
 *     sur un pool de dix connexions, et le quota de 300 requêtes par minute
 *     est commun à tous les postes derrière le proxy — la pointeuse comprise,
 *     club ouvert. Une carte démontée retire sa lecture de la file ; celle
 *     qui est en cours est annulée sur le réseau par son signal.
 */
import type { ClosingDetail, Etat } from './ClosingCommon.js'
import type { NightDetailResponse } from './nights-common.js'
import { chiffresDeLaCarte, type ChiffresCarte } from './night-sheet-model.js'

/** Ce qu'une carte sait de ses chiffres. `muet` : la carte n'écrit aucun montant, et dit pourquoi en aide. */
export type LectureCarte =
  | { etat: 'chargement' }
  | { etat: 'muet'; aide: string }
  | { etat: 'ok'; chiffres: ChiffresCarte }

/** Sans le droit de lire les montants, ou quand une lecture ne dit pas pourquoi elle a échoué. */
export const MONTANTS_NON_LUS = 'Montants non lus'

/** Les deux lectures de la fiche de nuit, annulables. */
export interface LecteursDeNuit {
  fiche: (date: string, signal: AbortSignal) => Promise<Etat<NightDetailResponse>>
  cloture: (id: string, signal: AbortSignal) => Promise<Etat<ClosingDetail>>
}

function muet<T>(e: Etat<T>): LectureCarte {
  return { etat: 'muet', aide: e.etat === 'absent' ? e.message : MONTANTS_NON_LUS }
}

/**
 * Les chiffres d'une carte, lus comme la fiche les lit : la fiche de la nuit,
 * puis — quand elle en nomme une — sa clôture. Une lecture qui échoue fait
 * taire la carte : jamais un chiffre calculé sur une autre base que la fiche.
 */
export async function lireChiffresDeCarte(date: string, lire: LecteursDeNuit, signal: AbortSignal): Promise<LectureCarte> {
  const fiche = await lire.fiche(date, signal)
  if (fiche.etat !== 'ok') return muet(fiche)
  const n = fiche.valeur
  if (!n.closing) return { etat: 'ok', chiffres: chiffresDeLaCarte(null, n) }
  const cloture = await lire.cloture(n.closing.id, signal)
  if (cloture.etat !== 'ok') return muet(cloture)
  return { etat: 'ok', chiffres: chiffresDeLaCarte(cloture.valeur, n) }
}

/**
 * Une file de lectures : au plus `max` en cours, les autres attendent leur
 * tour dans l'ordre d'arrivée. Une lecture dont le signal est annulé pendant
 * qu'elle attend ne part jamais et rend `null` ; en cours, elle reçoit
 * l'annulation par le même signal (sa requête est coupée) et libère sa place
 * en finissant. `null` = abandonnée : l'appelant n'écrit rien.
 */
export function fileDeLectures(max: number): <T>(tache: () => Promise<T>, signal: AbortSignal) => Promise<T | null> {
  let enCours = 0
  const attente: Array<() => void> = []
  // Une place qui se libère passe directement à la suivante.
  const liberer = () => {
    const suivante = attente.shift()
    if (suivante) suivante()
    else enCours -= 1
  }
  return async <T>(tache: () => Promise<T>, signal: AbortSignal): Promise<T | null> => {
    if (signal.aborted) return null
    if (enCours < max) enCours += 1
    else {
      const aSonTour = await new Promise<boolean>((resolve) => {
        const partir = () => { signal.removeEventListener('abort', renoncer); resolve(true) }
        const renoncer = () => {
          const i = attente.indexOf(partir)
          if (i >= 0) attente.splice(i, 1)
          resolve(false)
        }
        attente.push(partir)
        signal.addEventListener('abort', renoncer, { once: true })
      })
      if (!aSonTour) return null
    }
    try {
      return signal.aborted ? null : await tache()
    } finally {
      liberer()
    }
  }
}
